import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { api } from "../context/AuthContext";
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Box,
  Avatar,
  TextField,
  InputAdornment,
  Button,
  Pagination,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import { Search, Person, FilterList } from '@mui/icons-material';

function SuperAdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [page, setPage] = useState(1);

  useEffect(() => {
    async function fetchUsers() {
      try {
        const response = await api.get('/super-admin/users');
        setUsers(response.data);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchUsers();
  }, []);

  const handleToggleActive = async (userId, currentStatus) => {
    try {
      await api.patch(`/super-admin/users/${userId}/toggle-active`, { has_active_subscription: !currentStatus });
      setUsers(prevUsers =>
          prevUsers.map(user =>
              user.id === userId ? { ...user, has_active_subscription: !currentStatus } : user
          )
      );
    } catch (error) {
      console.error('Failed to update user status:', error);
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = (user.first_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.last_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.formatted_company_id?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (user.role?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesFilter = filterRole === 'All' ||
        (filterRole === 'Candidate' && user.role?.toLowerCase() === 'candidate') ||
        (filterRole === 'Admin' && user.role?.toLowerCase() === 'admin');

    return matchesSearch && matchesFilter;
  });

  const paginatedUsers = filteredUsers.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(filteredUsers.length / 10);

  if (loading) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
    );
  }

  return (
      <Container sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "flex-start", mb: 4 }}>
          <Box>
              {/*<Typography
              sx={{
                color: "#1E3A8A",
                fontSize: {
                  xs: "2rem",
                  sm: "2.2rem",
                  md: "2.5rem",
                  lg: "2.8rem",
                },
                fontWeight: 700,
                lineHeight: 1.2,
                mb: 0.5,
              }}
            >*/}
              <Typography
                  variant="h4"
                  sx={{
                      color: "#1E3A8A",
                      fontWeight: "bold",
                      fontSize: { xs: "1.5rem", md: "2rem" }
                  }}
              >
              System Users
            </Typography>

            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "1rem",
              }}
            >
              View, search, and manage all registered users across the platform.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                  ),
                }}
                sx={{ width: 300 }}
            />
            <FormControl size="small">
              <Select
                  value={filterRole}
                  onChange={(e) => {
                    setFilterRole(e.target.value);
                    setPage(1);
                  }}
                  displayEmpty
                  renderValue={() => (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                        <FilterList sx={{ fontSize: 20 }} />
                        Filters
                      </Box>
                  )}
                  sx={{
                    borderRadius: 0,
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1E3A8A',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1E40AF',
                    },
                    '& .MuiSelect-select': {
                      display: 'flex',
                      alignItems: 'center',
                    }
                  }}
              >
                <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                  Filters
                </MenuItem>
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Candidate">Candidate</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 3 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>User</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Subscription</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Joined Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedUsers.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {user.first_name ? user.first_name[0] : <Person />}
                        </Avatar>
                        <Typography variant="body2">
                          {user.first_name} {user.last_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                          label={user.role}
                          color={user.role === 'super_admin' ? 'error' : (user.role === 'admin' ? 'primary' : 'default')}
                          size="small"
                          variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                          label={user.has_active_subscription ? 'Active' : 'Inactive'}
                          color={user.has_active_subscription ? 'success' : 'default'}
                          size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                          size="small"
                          variant={user.has_active_subscription ? "outlined" : "contained"}
                          color={user.has_active_subscription ? "error" : "success"}
                          onClick={() => handleToggleActive(user.id, user.has_active_subscription)}
                      >
                        {user.has_active_subscription ? "Deactivate" : "Activate"}
                      </Button>
                    </TableCell>
                  </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No users found</Typography>
                    </TableCell>
                  </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 3,
              flexWrap: "wrap",
              gap: 2,
            }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {paginatedUsers.length > 0 ? (page - 1) * 10 + 1 : 0} to{" "}
            {Math.min(page * 10, filteredUsers.length)} of{" "}
            {filteredUsers.length} records
          </Typography>
          <Pagination
              count={totalPages}
              page={page}
              onChange={(e, value) => setPage(value)}
              shape="rounded"
              color="primary"
              sx={{
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: "#1E3A8A",
                  "&:hover": {
                    backgroundColor: "#1E40AF",
                  },
                },
              }}
          />
        </Box>
      </Container>
  );
}

export default SuperAdminUsers;
